import {
  formatCurrency,
  formatNumber,
  formatPercent,
  toDateShort,
  toDateTimeShort,
} from '@monetize/utils/core';
import { isValid } from 'date-fns/isValid';
import { parseISO } from 'date-fns/parseISO';
import lodashGet from 'lodash/get';
import isNil from 'lodash/isNil';
import { evaluateFormula } from './formula/evaluator';
import { buildTemplateFormulaScope } from './formula/template-formula-scope';
import { resolveTemplateFieldPaths } from './resolve-field-path';
import {
  parseTemplateExpression,
  TemplateExpressionDataFormat,
  TemplateExpressionFormatOptions,
} from './template-expression-parser';

/**
 * Pino-compatible logger surface. This library runs in both the browser and
 * on the server, so logging is injected by the caller instead of importing a
 * server-only logger; it defaults to a no-op.
 */
export type TemplateExpressionLogger = {
  warn: (obj: unknown, msg?: string, ...args: unknown[]) => void;
};

const NOOP_LOGGER: TemplateExpressionLogger = {
  warn: () => undefined,
};

/**
 * Matches each `{{...}}` chunk in template content. Returned as a factory
 * because the `g` flag makes RegExp instances stateful.
 */
export function createTemplateExpressionRegex(): RegExp {
  return /{{(.*?)}}/gs;
}

/**
 * Decode the HTML entities rich-text editors (TinyMCE) introduce inside
 * template expressions, e.g. `&gt;`/`&lt;` around filter operators, `&amp;`
 * in `&&`, `&quot;`/`&#39;` around string literals, and `&nbsp;` for
 * consecutive spaces. `&amp;` must be decoded last so sequences like
 * `&amp;gt;` are not double-decoded.
 */
export function decodeTemplateExpressionEntities(value: string): string {
  return value
    .replaceAll('&gt;', '>')
    .replaceAll('&lt;', '<')
    .replaceAll('&quot;', '"')
    .replaceAll('&#39;', "'")
    .replaceAll('&nbsp;', ' ')
    .replaceAll('\u00a0', ' ')
    .replaceAll('&amp;', '&');
}

/**
 *
 * For a given template string (e.g. conditional terms), replace template expressions with values.
 * Refer to @monetize/expression-parser for the expression format and test cases.
 */
export function replaceTemplateExpressionsWithValue({
  content,
  record,
  currencyFieldPath,
  locale = 'en-US',
  logger = NOOP_LOGGER,
}: {
  /**
   * Template with replacement strings, e.g. terms and conditions
   */
  content: string;
  /**
   * Record that contains the data to replace within the template content
   */
  record: unknown;
  /**
   * If format is currency, this is the path on the record to where the currency is obtained from
   * will default to USD if not provided
   */
  currencyFieldPath?: string;
  locale?: string;
  logger?: TemplateExpressionLogger;
}) {
  // Detach-safe wrapper — pino log methods must be called on the logger.
  const warn: TemplateExpressionLogger['warn'] = (obj, msg, ...args) =>
    logger.warn(obj, msg, ...args);

  // Use `replace` with a callback, returning a new string (doesn't mutate the original).
  return content.replace(createTemplateExpressionRegex(), (match) => {
    try {
      match = decodeTemplateExpressionEntities(match.trim());
      const { fieldPaths, formula, format, formatOptions } =
        parseTemplateExpression(match);

      let replacementValue: unknown;
      if (formula) {
        replacementValue = evaluateFormula(
          formula.ast,
          buildTemplateFormulaScope({ record, warn }),
        );
        if (
          typeof replacementValue === 'number' &&
          !Number.isFinite(replacementValue)
        ) {
          logger.warn(
            { value: match },
            'Formula did not produce a finite number',
          );
          return '';
        }
      } else {
        replacementValue = resolveTemplateFieldPaths({
          record,
          fieldPaths,
          warn,
        });
      }

      // TODO: also detect if object or other incompatible data type
      if (isNil(replacementValue) || replacementValue === '') {
        return '';
      }

      return escapeHtml(
        formatReplacementValue({
          value: replacementValue,
          format,
          formatOptions,
          record,
          currencyFieldPath,
          locale,
        }),
      );
    } catch (ex) {
      logger.warn(
        { value: match, err: ex },
        'Could not parse or process template expression',
      );
    }
    return '';
  });
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Escape a resolved value before it is spliced into the surrounding rich-text
 * markup. Terms are rendered as HTML in both the PDF and the in-app preview
 * (`dangerouslySetInnerHTML`), and the values interpolated into them —
 * account names, quote fields — are user-supplied and can arrive via CRM
 * sync, so without this they can break the surrounding markup or inject
 * their own. Only the substituted value is escaped; the author's term markup
 * is left intact.
 */
function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}

function formatReplacementValue({
  value,
  format,
  formatOptions,
  record,
  currencyFieldPath,
  locale,
}: {
  value: unknown;
  format: TemplateExpressionDataFormat | null;
  formatOptions: TemplateExpressionFormatOptions | null;
  record: unknown;
  currencyFieldPath?: string;
  locale: string;
}): string {
  switch (format) {
    case 'CURRENCY': {
      const currency = currencyFieldPath
        ? lodashGet(record, currencyFieldPath, 'USD')
        : 'USD';
      const valueAsNumber = Number(value);
      // If invalid number, fallback to displaying the value as-is
      if (isNaN(valueAsNumber)) {
        return String(value);
      }
      return (
        formatCurrency(valueAsNumber, {
          currency,
          locale,
          minimumFractionDigits:
            formatOptions?.minimumFractionDigits ?? undefined,
          maximumFractionDigits:
            formatOptions?.maximumFractionDigits ?? undefined,
        })
          // currency formatters use non-standard whitespace characters
          .replaceAll('\u00a0', ' ')
      );
    }
    case 'DATE':
    case 'DATE_TIME': {
      const date = parseISO(value as string);
      // If invalid date, fallback to displaying the value as-is
      if (!isValid(date)) {
        return String(value);
      }
      if (format === 'DATE') {
        return toDateShort(date);
      }
      return toDateTimeShort(date);
    }
    case 'NUMBER': {
      const valueAsNumber = Number(value);
      // If invalid number, fallback to displaying the value as-is
      if (isNaN(valueAsNumber)) {
        return String(value);
      }
      return formatNumber(valueAsNumber, {
        locale,
        minimumFractionDigits: formatOptions?.minimumFractionDigits ?? 0,
        maximumFractionDigits:
          formatOptions?.maximumFractionDigits ?? undefined,
      });
    }
    case 'PERCENT': {
      const valueAsNumber = Number(value);
      // If invalid number, fallback to displaying the value as-is
      if (isNaN(valueAsNumber)) {
        return String(value);
      }
      return formatPercent(valueAsNumber, {
        locale,
        minimumFractionDigits: formatOptions?.minimumFractionDigits ?? 0,
        maximumFractionDigits:
          formatOptions?.maximumFractionDigits ?? undefined,
      });
    }
    default:
      break;
  }
  return String(value);
}
